import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { GRUPO_OPTIONS, GRUPO_LABELS, STATUS_LABELS, formatDate, todayISO, parseLocalDate } from "@/lib/constants";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import LoadError from "@/components/LoadError";
import {
  Heart,
  Syringe,
  Baby,
  FirstAidKit,
  Drop,
  Scales,
  FileArrowDown,
  ArrowLeft,
  Trash,
  Warning,
  X,
  PencilSimple,
  TestTube,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const TABS = [
  { key: "resumo", label: "Resumo" },
  { key: "cios", label: "Cios" },
  { key: "inseminacoes", label: "Inseminações" },
  { key: "confirmacoes_prenhez", label: "Prenhez" },
  { key: "partos", label: "Partos" },
  { key: "tratamentos", label: "Tratamentos" },
  { key: "producoes", label: "Produção" },
  { key: "pesos", label: "Pesagens" },
  { key: "ccs", label: "Sanidade / CCS" },
];

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#4E544B] block mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

const roleForm = {
  cios: ["admin", "veterinario", "tecnico", "monitor"],
  inseminacoes: ["admin", "veterinario", "tecnico"],
  partos: ["admin", "veterinario", "tecnico"],
  tratamentos: ["admin", "veterinario"],
  producoes: ["admin", "veterinario", "tecnico", "monitor"],
  pesos: ["admin", "veterinario", "tecnico", "monitor"],
  ccs: ["admin", "veterinario", "tecnico"],
  confirmacoes_prenhez: ["admin", "veterinario", "tecnico"],
};

function EventForm({ tab, animalId, onSaved, initial, editingId, onCancelEdit }) {
  const { user } = useAuth();
  const emptyF = { data: todayISO() };
  const [f, setF] = useState(initial || emptyF);
  const [saving, setSaving] = useState(false);
  const can = roleForm[tab]?.includes(user.role);

  useEffect(() => {
    setF(initial || emptyF);
    // eslint-disable-next-line
  }, [editingId, tab]);

  if (!can) return null;

  const isEdit = !!editingId;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...f };
      if (!isEdit) payload.animal_id = animalId;
      if (tab === "producoes") payload.litros = parseFloat(payload.litros);
      if (tab === "ccs") payload.ccs_mil_ml = parseFloat(payload.ccs_mil_ml);
      if (tab === "confirmacoes_prenhez" && payload.dias_gestacao !== undefined && payload.dias_gestacao !== "" && payload.dias_gestacao !== null) {
        payload.dias_gestacao = parseInt(payload.dias_gestacao);
      } else if (tab === "confirmacoes_prenhez") {
        payload.dias_gestacao = null;
      }
      if (tab === "pesos") {
        payload.peso_kg = parseFloat(payload.peso_kg);
        if (payload.condicao_corporal !== "" && payload.condicao_corporal != null)
          payload.condicao_corporal = parseFloat(payload.condicao_corporal);
        else payload.condicao_corporal = null;
      }
      if (tab === "tratamentos" && payload.periodo_carencia_dias !== undefined)
        payload.periodo_carencia_dias = parseInt(payload.periodo_carencia_dias || 0);
      if (isEdit) {
        // Strip fields the backend rejects on PATCH
        delete payload.animal_id;
        delete payload.id;
        delete payload.created_at;
        delete payload.created_by;
        delete payload.criar_bezerro;
        await api.patch(`/${tab}/${editingId}`, payload);
        toast.success("Registro atualizado");
      } else {
        await api.post(`/${tab}`, payload);
        toast.success("Registro salvo");
      }
      setF(emptyF);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      data-testid={`form-${tab}`}
      onSubmit={submit}
      className="bg-white border border-[#E2E0D8] rounded-sm p-4 grid grid-cols-1 md:grid-cols-3 gap-3 mb-6"
    >
      <Field label="Data *">
        <input
          type="date"
          required
          value={f.data || ""}
          onChange={(e) => set("data", e.target.value)}
          className="input"
        />
      </Field>

      {tab === "cios" && (
        <div className="md:col-span-2">
          <Field label="Observações">
            <input
              value={f.observacoes || ""}
              onChange={(e) => set("observacoes", e.target.value)}
              className="input"
              placeholder="Sinais, intensidade…"
            />
          </Field>
        </div>
      )}

      {tab === "inseminacoes" && (
        <>
          <Field label="Touro *">
            <input
              required
              data-testid="ins-touro"
              value={f.touro || ""}
              onChange={(e) => set("touro", e.target.value)}
              className="input"
              placeholder="Código do sêmen / touro"
            />
          </Field>
          <Field label="Técnica">
            <select
              value={f.tecnica || "IA"}
              onChange={(e) => set("tecnica", e.target.value)}
              className="input"
            >
              <option value="IA">IA convencional</option>
              <option value="IATF">IATF</option>
              <option value="MONTA">Monta natural</option>
            </select>
          </Field>
          <Field label="Inseminador">
            <input
              value={f.inseminador || ""}
              onChange={(e) => set("inseminador", e.target.value)}
              className="input"
            />
          </Field>
        </>
      )}

      {tab === "partos" && (
        <>
          <Field label="Nº do bezerro">
            <input
              value={f.bezerro_numero || ""}
              onChange={(e) => set("bezerro_numero", e.target.value)}
              className="input"
              placeholder="Novo brinco"
            />
          </Field>
          <Field label="Sexo do bezerro">
            <select
              value={f.bezerro_sexo || "FEMEA"}
              onChange={(e) => set("bezerro_sexo", e.target.value)}
              className="input"
            >
              <option value="FEMEA">Fêmea</option>
              <option value="MACHO">Macho</option>
            </select>
          </Field>
          <Field label="Touro pai">
            <input
              value={f.pai_touro || ""}
              onChange={(e) => set("pai_touro", e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Dificuldade">
            <select
              value={f.dificuldade || "normal"}
              onChange={(e) => set("dificuldade", e.target.value)}
              className="input"
            >
              <option value="normal">Normal</option>
              <option value="distocico">Distócico</option>
              <option value="cesarea">Cesárea</option>
            </select>
          </Field>
        </>
      )}

      {tab === "tratamentos" && (
        <>
          <Field label="Tipo *">
            <input
              required
              value={f.tipo || ""}
              onChange={(e) => set("tipo", e.target.value)}
              className="input"
              placeholder="Ex: Mastite, Vacina"
            />
          </Field>
          <Field label="Medicamento">
            <input
              value={f.medicamento || ""}
              onChange={(e) => set("medicamento", e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Dosagem">
            <input
              value={f.dosagem || ""}
              onChange={(e) => set("dosagem", e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Veterinário">
            <input
              value={f.veterinario || ""}
              onChange={(e) => set("veterinario", e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Carência (dias)">
            <input
              type="number"
              min={0}
              value={f.periodo_carencia_dias || 0}
              onChange={(e) => set("periodo_carencia_dias", e.target.value)}
              className="input"
            />
          </Field>
        </>
      )}

      {tab === "producoes" && (
        <>
          <Field label="Ordenha">
            <select
              value={f.ordenha || "TOTAL"}
              onChange={(e) => set("ordenha", e.target.value)}
              className="input"
            >
              <option value="MANHA">Manhã</option>
              <option value="TARDE">Tarde</option>
              <option value="NOITE">Noite</option>
              <option value="TOTAL">Total do dia</option>
            </select>
          </Field>
          <Field label="Litros *">
            <input
              type="number"
              step="0.1"
              min={0}
              required
              data-testid="prod-litros"
              value={f.litros || ""}
              onChange={(e) => set("litros", e.target.value)}
              className="input"
            />
          </Field>
        </>
      )}

      {tab === "pesos" && (
        <>
          <Field label="Peso (kg) *">
            <input
              type="number"
              step="0.1"
              min={0}
              required
              data-testid="peso-kg"
              value={f.peso_kg || ""}
              onChange={(e) => set("peso_kg", e.target.value)}
              className="input"
              placeholder="Ex: 480.5"
            />
          </Field>
          <Field label="Cond. corporal (1-5)">
            <input
              type="number"
              step="0.25"
              min={1}
              max={5}
              value={f.condicao_corporal || ""}
              onChange={(e) => set("condicao_corporal", e.target.value)}
              className="input"
              placeholder="Ex: 3.25"
            />
          </Field>
          <Field label="Método">
            <select
              value={f.metodo || "BALANCA"}
              onChange={(e) => set("metodo", e.target.value)}
              className="input"
            >
              <option value="BALANCA">Balança</option>
              <option value="FITA">Fita bovinométrica</option>
              <option value="ESTIMADO">Estimado</option>
            </select>
          </Field>
          <div className="md:col-span-3">
            <Field label="Observações">
              <input
                value={f.observacoes || ""}
                onChange={(e) => set("observacoes", e.target.value)}
                className="input"
              />
            </Field>
          </div>
        </>
      )}

      {tab === "ccs" && (
        <>
          <Field label="CCS (mil cél/mL) *">
            <input
              type="number"
              step="1"
              min={0}
              required
              data-testid="ccs-valor"
              value={f.ccs_mil_ml || ""}
              onChange={(e) => set("ccs_mil_ml", e.target.value)}
              className="input"
              placeholder="Ex: 250"
            />
          </Field>
          <Field label="Quarto mamário">
            <select
              data-testid="ccs-quarto"
              value={f.quarto || "GERAL"}
              onChange={(e) => set("quarto", e.target.value)}
              className="input"
            >
              <option value="GERAL">Geral / composto</option>
              <option value="AD">Anterior direito</option>
              <option value="AE">Anterior esquerdo</option>
              <option value="PD">Posterior direito</option>
              <option value="PE">Posterior esquerdo</option>
            </select>
          </Field>
          <Field label="Observações">
            <input
              value={f.observacoes || ""}
              onChange={(e) => set("observacoes", e.target.value)}
              className="input"
              placeholder="Ex: CMT positivo (++)"
            />
          </Field>
        </>
      )}

      {tab === "confirmacoes_prenhez" && (
        <>
          <Field label="Resultado *">
            <select
              data-testid="prenhez-resultado"
              value={f.resultado || "POSITIVA"}
              onChange={(e) => set("resultado", e.target.value)}
              className="input"
              required
            >
              <option value="POSITIVA">Positiva (prenhe)</option>
              <option value="NEGATIVA">Negativa (vazia)</option>
              <option value="INCONCLUSIVA">Inconclusiva</option>
            </select>
          </Field>
          <Field label="Método">
            <select
              value={f.metodo || ""}
              onChange={(e) => set("metodo", e.target.value)}
              className="input"
            >
              <option value="">— selecionar —</option>
              <option value="Palpação retal">Palpação retal</option>
              <option value="Ultrassom">Ultrassom</option>
              <option value="Exame de sangue">Exame de sangue</option>
              <option value="Exame de leite">Exame de leite</option>
            </select>
          </Field>
          <Field label="Dias de gestação">
            <input
              type="number"
              min={0}
              value={f.dias_gestacao || ""}
              onChange={(e) => set("dias_gestacao", e.target.value)}
              className="input"
              placeholder="Ex: 45"
            />
          </Field>
          <Field label="Veterinário">
            <input
              value={f.veterinario || ""}
              onChange={(e) => set("veterinario", e.target.value)}
              className="input"
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="Observações">
              <input
                value={f.observacoes || ""}
                onChange={(e) => set("observacoes", e.target.value)}
                className="input"
              />
            </Field>
          </div>
        </>
      )}

      <div className="md:col-span-3 flex justify-end gap-2">
        {isEdit && (
          <button
            type="button"
            data-testid={`cancel-edit-${tab}`}
            onClick={() => {
              setF(emptyF);
              onCancelEdit?.();
            }}
            className="btn-secondary"
          >
            Cancelar edição
          </button>
        )}
        <button
          data-testid={`submit-${tab}`}
          type="submit"
          disabled={saving}
          className="btn-primary"
        >
          {saving ? "Salvando…" : isEdit ? "Salvar alterações" : "Registrar"}
        </button>
      </div>
    </form>
  );
}

function EventTable({ tab, rows, onEdit, onDelete, canEdit }) {
  if (!rows || rows.length === 0)
    return (
      <div className="text-sm text-[#4E544B] p-6 bg-white border border-[#E2E0D8] rounded-sm">
        Nenhum registro ainda.
      </div>
    );

  const cols = {
    cios: [
      ["data", "Data", (v) => formatDate(v)],
      ["observacoes", "Observações"],
    ],
    inseminacoes: [
      ["data", "Data", (v) => formatDate(v)],
      ["touro", "Touro"],
      ["tecnica", "Técnica"],
      ["inseminador", "Inseminador"],
    ],
    partos: [
      ["data", "Data", (v) => formatDate(v)],
      ["bezerro_numero", "Bezerro"],
      ["bezerro_sexo", "Sexo"],
      ["pai_touro", "Touro pai"],
      ["dificuldade", "Dificuldade"],
    ],
    tratamentos: [
      ["data", "Data", (v) => formatDate(v)],
      ["tipo", "Tipo"],
      ["medicamento", "Medicamento"],
      ["dosagem", "Dosagem"],
      ["veterinario", "Veterinário"],
    ],
    producoes: [
      ["data", "Data", (v) => formatDate(v)],
      ["ordenha", "Ordenha"],
      ["litros", "Litros", (v) => Number(v).toFixed(2)],
    ],
    pesos: [
      ["data", "Data", (v) => formatDate(v)],
      ["peso_kg", "Peso (kg)", (v) => Number(v).toFixed(1)],
      ["condicao_corporal", "ECC", (v) => (v ? Number(v).toFixed(2) : "-")],
      ["metodo", "Método"],
      ["observacoes", "Observações"],
    ],
    ccs: [
      ["data", "Data", (v) => formatDate(v)],
      ["ccs_mil_ml", "CCS (mil/mL)", (v) => Number(v).toFixed(0)],
      ["quarto", "Quarto", (v) => ({
        GERAL: "Geral", AD: "Ant. dir.", AE: "Ant. esq.",
        PD: "Post. dir.", PE: "Post. esq.",
      }[v] || v || "-")],
      ["_classificacao", "Classificação", (_, row) => {
        const val = Number(row?.ccs_mil_ml || 0);
        if (val >= 500) return "Alta";
        if (val >= 200) return "Suspeita";
        return "Normal";
      }],
      ["observacoes", "Observações"],
    ],
    confirmacoes_prenhez: [
      ["data", "Data", (v) => formatDate(v)],
      ["resultado", "Resultado", (v) => ({
        POSITIVA: "Positiva", NEGATIVA: "Negativa", INCONCLUSIVA: "Inconclusiva",
      }[v] || v || "-")],
      ["metodo", "Método"],
      ["dias_gestacao", "Dias gest.", (v) => (v ? `${v}d` : "-")],
      ["veterinario", "Veterinário"],
      ["observacoes", "Observações"],
    ],
  }[tab];

  return (
    <div className="bg-white border border-[#E2E0D8] rounded-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F6F5F2] border-b border-[#E2E0D8]">
            <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#4E544B]">
              {cols.map((c) => (
                <th key={c[0]} className="px-4 py-3">
                  {c[1]}
                </th>
              ))}
              {canEdit && <th className="px-4 py-3 text-right w-24">Ações</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#E2E0D8]">
                {cols.map((c) => (
                  <td key={c[0]} className="px-4 py-3">
                    {c[2] ? c[2](r[c[0]], r) : r[c[0]] || "-"}
                  </td>
                ))}
                {canEdit && (
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        data-testid={`edit-${tab}-${r.id}`}
                        onClick={() => onEdit?.(r)}
                        className="p-1.5 rounded-sm text-[#4E544B] hover:bg-[#F6F5F2] hover:text-[#2A5A3B]"
                        aria-label="Editar"
                        title="Editar"
                      >
                        <PencilSimple size={16} weight="bold" />
                      </button>
                      <button
                        data-testid={`delete-${tab}-${r.id}`}
                        onClick={() => onDelete?.(r)}
                        className="p-1.5 rounded-sm text-[#4E544B] hover:bg-[#FDEDED] hover:text-[#C05746]"
                        aria-label="Excluir"
                        title="Excluir"
                      >
                        <Trash size={16} weight="bold" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AnimalDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [animal, setAnimal] = useState(null);
  const [history, setHistory] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState("resumo");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null); // row being edited in current tab

  async function load() {
    setLoadError(null);
    try {
      const [a, h] = await Promise.all([
        api.get(`/animals/${id}`),
        api.get(`/animals/${id}/history`),
      ]);
      setAnimal(a.data);
      setHistory(h.data);
    } catch (e) {
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  // reset event editor when switching tabs
  useEffect(() => {
    setEditingEvent(null);
  }, [tab]);

  if (loadError && !animal)
    return <LoadError error={loadError} onRetry={load} />;
  if (!animal) return <div className="text-[#4E544B]">Carregando animal…</div>;

  async function handleDelete() {
    setDeleting(true);
    try {
      await api.delete(`/animals/${id}`);
      toast.success(`Animal ${animal.numero} removido`);
      nav("/animais");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setDeleting(false);
    }
  }

  async function handleStatusChange(newStatus) {
    try {
      await api.patch(`/animals/${id}`, { status: newStatus });
      toast.success("Status atualizado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function downloadPdf() {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/reports/animal/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Falha ao gerar PDF (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ficha_${animal.numero}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  }

  return (
    <div data-testid="animal-detail" className="space-y-6">
      <button
        onClick={() => nav("/animais")}
        className="text-sm text-[#4E544B] hover:text-[#1A1C19] inline-flex items-center gap-2"
      >
        <ArrowLeft size={14} /> voltar para animais
      </button>

      <div className="bg-white border border-[#E2E0D8] rounded-sm p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#4E544B]">
              Animal
            </div>
            <h1 className="font-heading font-black text-4xl tracking-tighter mt-1">
              {animal.numero}
              {animal.nome && (
                <span className="text-[#4E544B] font-bold text-2xl ml-3">
                  {animal.nome}
                </span>
              )}
            </h1>
            <div className="text-sm text-[#4E544B] mt-2">
              {GRUPO_LABELS[animal.grupo]} · {animal.sexo === "FEMEA" ? "Fêmea" : "Macho"}
              {animal.raca && ` · ${animal.raca}`}
              {animal.data_nascimento &&
                ` · Nasceu em ${formatDate(animal.data_nascimento)}`}
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="bg-[#F6F5F2] border border-[#E2E0D8] rounded-sm px-4 py-3">
              <div className="text-[11px] uppercase tracking-wider text-[#4E544B]">
                Dias em lactação
              </div>
              <div className="font-heading font-black text-2xl text-[#2A5A3B]">
                {animal.del !== null && animal.del !== undefined ? animal.del : "-"}
              </div>
            </div>
            <div
              data-testid="ultimo-peso-card"
              className="bg-[#F6F5F2] border border-[#E2E0D8] rounded-sm px-4 py-3"
            >
              <div className="text-[11px] uppercase tracking-wider text-[#4E544B]">
                Último peso
              </div>
              <div className="font-heading font-black text-2xl text-[#2A5A3B]">
                {animal.ultimo_peso
                  ? `${Number(animal.ultimo_peso.peso_kg).toFixed(1)} kg`
                  : "-"}
              </div>
              {animal.ultimo_peso && (
                <div className="text-[10px] text-[#4E544B]">
                  em {formatDate(animal.ultimo_peso.data)}
                </div>
              )}
            </div>
            <button
              data-testid="download-pdf"
              onClick={downloadPdf}
              className="btn-secondary inline-flex items-center gap-2 self-start"
            >
              <FileArrowDown size={16} weight="bold" /> Ficha PDF
            </button>
            {["admin", "veterinario", "tecnico"].includes(user.role) && (
              <button
                data-testid="edit-animal-btn"
                onClick={() => setEditing(true)}
                className="btn-primary inline-flex items-center gap-2 self-start"
              >
                <PencilSimple size={16} weight="bold" /> Editar
              </button>
            )}
            {user.role === "admin" && (
              <button
                data-testid="delete-animal-btn"
                onClick={() => setConfirmDelete(true)}
                className="inline-flex items-center gap-2 self-start border border-[#C05746] text-[#C05746] hover:bg-[#FDEDED] font-bold px-4 py-2 rounded-sm text-sm transition-colors"
              >
                <Trash size={16} weight="bold" /> Excluir
              </button>
            )}
          </div>
        </div>

        {["admin", "veterinario", "tecnico"].includes(user.role) && (
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-dashed border-[#E2E0D8]">
            <span className="text-[11px] uppercase tracking-[0.15em] text-[#4E544B]">
              Alterar status:
            </span>
            {["ATIVO", "VENDIDO", "MORTO", "DESCARTADO"].map((s) => (
              <button
                key={s}
                data-testid={`status-${s}`}
                onClick={() => handleStatusChange(s)}
                disabled={animal.status === s}
                className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
                  animal.status === s
                    ? "bg-[#2A5A3B] text-white border-[#2A5A3B] cursor-default"
                    : "bg-white border-[#E2E0D8] text-[#4E544B] hover:bg-[#F6F5F2]"
                }`}
              >
                {s === "ATIVO" ? "Ativo" : s === "VENDIDO" ? "Vendido" : s === "MORTO" ? "Morto" : "Descartado"}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-[#E2E0D8]">
          <Info label="Mãe" value={animal.mae ? `${animal.mae.numero}${animal.mae.nome ? " - " + animal.mae.nome : ""}` : "-"} />
          <Info label="Touro (pai)" value={animal.pai_touro || "-"} />
          <Info label="Status" value={animal.status || "-"} />
          <Info
            label="Filhas"
            value={history?.filhas?.length ? history.filhas.length : 0}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto border-b border-[#E2E0D8] gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-3 text-sm font-bold tracking-wide whitespace-nowrap border-b-2 transition-colors ${
              tab === t.key
                ? "border-[#2A5A3B] text-[#2A5A3B]"
                : "border-transparent text-[#4E544B] hover:text-[#1A1C19]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "resumo" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <SummaryCard icon={Heart} label="Cios" count={history?.cios?.length || 0} />
          <SummaryCard
            icon={Syringe}
            label="Inseminações"
            count={history?.inseminacoes?.length || 0}
          />
          <SummaryCard icon={Baby} label="Partos" count={history?.partos?.length || 0} />
          <SummaryCard
            icon={FirstAidKit}
            label="Tratamentos"
            count={history?.tratamentos?.length || 0}
          />
          <SummaryCard
            icon={Drop}
            label="Registros de produção"
            count={history?.producoes?.length || 0}
          />
          <SummaryCard
            icon={Scales}
            label="Pesagens"
            count={history?.pesos?.length || 0}
          />
          <SummaryCard
            icon={TestTube}
            label="Leituras de CCS"
            count={history?.ccs?.length || 0}
          />
          <SummaryCard
            icon={Baby}
            label="Confirmações de prenhez"
            count={history?.confirmacoes_prenhez?.length || 0}
          />
          {history?.filhas?.length > 0 && (
            <div className="bg-white border border-[#E2E0D8] rounded-sm p-5 md:col-span-2 lg:col-span-3">
              <div className="text-[11px] uppercase tracking-[0.15em] text-[#4E544B] mb-3">
                Filhas
              </div>
              <div className="flex flex-wrap gap-2">
                {history.filhas.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => nav(`/animais/${f.id}`)}
                    className="text-sm border border-[#E2E0D8] px-3 py-1.5 rounded-sm hover:bg-[#F6F5F2]"
                  >
                    {f.numero} {f.nome ? `- ${f.nome}` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab !== "resumo" && (() => {
        const canRowEdit = roleForm[tab]?.includes(user.role);
        const initialForEdit = editingEvent
          ? {
              ...editingEvent,
              data: (editingEvent.data || "").slice(0, 10),
            }
          : null;
        return (
          <>
            <EventForm
              tab={tab}
              animalId={id}
              initial={initialForEdit}
              editingId={editingEvent?.id || null}
              onCancelEdit={() => setEditingEvent(null)}
              onSaved={() => {
                setEditingEvent(null);
                load();
              }}
            />
            {tab === "pesos" && <PesoChart pesos={history?.pesos} />}
            {tab === "producoes" && (
              <LactationChart
                producoes={history?.producoes}
                partos={history?.partos}
              />
            )}
            {tab === "ccs" && <CCSChart ccs={history?.ccs} />}
            <EventTable
              tab={tab}
              rows={history?.[tab]}
              canEdit={canRowEdit}
              onEdit={(row) => {
                setEditingEvent(row);
                if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              onDelete={async (row) => {
                if (!window.confirm("Excluir este registro?")) return;
                try {
                  await api.delete(`/${tab}/${row.id}`);
                  toast.success("Registro removido");
                  if (editingEvent?.id === row.id) setEditingEvent(null);
                  load();
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                }
              }}
            />
          </>
        );
      })()}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-[#1A1C19]/60 flex items-center justify-center p-4">
          <div
            data-testid="delete-modal"
            className="bg-white border border-[#E2E0D8] rounded-sm w-full max-w-md"
          >
            <div className="flex items-center justify-between p-5 border-b border-[#E2E0D8]">
              <div className="flex items-center gap-2">
                <Warning size={22} weight="duotone" color="#C05746" />
                <h3 className="font-heading font-bold text-xl tracking-tight">
                  Excluir animal
                </h3>
              </div>
              <button
                onClick={() => setConfirmDelete(false)}
                className="p-1"
                aria-label="Fechar"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <p>
                Tem certeza que deseja excluir o animal{" "}
                <b>
                  {animal.numero}
                  {animal.nome ? ` — ${animal.nome}` : ""}
                </b>
                ?
              </p>
              <div className="bg-[#FDEDED] border border-[#C05746]/20 text-[#C05746] rounded-sm p-3 text-xs">
                Essa ação também remove permanentemente <b>todos os registros</b>{" "}
                relacionados: cios, inseminações, partos, tratamentos, pesagens e
                produção. Não é possível desfazer.
              </div>
              <p className="text-[#4E544B] text-xs">
                Se preferir apenas marcar o animal como Vendido / Morto /
                Descartado, feche este aviso e use as opções de status acima.
              </p>
            </div>
            <div className="p-5 border-t border-[#E2E0D8] flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(false)}
                className="btn-secondary"
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                data-testid="confirm-delete-animal"
                onClick={handleDelete}
                disabled={deleting}
                className="bg-[#C05746] hover:bg-[#A04030] disabled:opacity-60 text-white font-bold px-5 py-2 rounded-sm text-sm inline-flex items-center gap-2"
              >
                <Trash size={16} weight="bold" />
                {deleting ? "Excluindo…" : "Excluir permanentemente"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <EditAnimalModal
          animal={animal}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function EditAnimalModal({ animal, onClose, onSaved }) {
  const [f, setF] = useState({
    numero: animal.numero || "",
    nome: animal.nome || "",
    sexo: animal.sexo || "FEMEA",
    raca: animal.raca || "",
    data_nascimento: (animal.data_nascimento || "").slice(0, 10),
    grupo: animal.grupo || "BEZERRA",
    status: animal.status || "ATIVO",
    mae_id: animal.mae_id || "",
    pai_touro: animal.pai_touro || "",
    observacoes: animal.observacoes || "",
  });
  const [mothers, setMothers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/animals", { params: { status: "ATIVO" } })
      .then((r) =>
        setMothers(
          r.data.filter((m) => m.sexo === "FEMEA" && m.id !== animal.id),
        ),
      )
      .catch(() => setMothers([]));
  }, [animal.id]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...f };
      // send empty strings as null so backend clears the field
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (!payload.numero) throw new Error("Número obrigatório");
      await api.patch(`/animals/${animal.id}`, payload);
      toast.success("Animal atualizado");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  const Fld = ({ label, children }) => (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#4E544B] block mb-2">
        {label}
      </span>
      {children}
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 bg-[#1A1C19]/60 flex items-center justify-center p-4">
      <div
        data-testid="edit-animal-modal"
        className="bg-white border border-[#E2E0D8] rounded-sm w-full max-w-2xl max-h-[90vh] overflow-auto"
      >
        <div className="flex items-center justify-between p-5 border-b border-[#E2E0D8] sticky top-0 bg-white">
          <h3 className="font-heading font-bold text-xl tracking-tight">
            Editar animal
          </h3>
          <button onClick={onClose} className="p-1" aria-label="Fechar">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Fld label="Número / brinco *">
            <input
              data-testid="edit-numero"
              required
              value={f.numero}
              onChange={(e) => set("numero", e.target.value)}
              className="input"
            />
          </Fld>
          <Fld label="Nome">
            <input
              data-testid="edit-nome"
              value={f.nome}
              onChange={(e) => set("nome", e.target.value)}
              className="input"
            />
          </Fld>
          <Fld label="Sexo">
            <select
              data-testid="edit-sexo"
              value={f.sexo}
              onChange={(e) => set("sexo", e.target.value)}
              className="input"
            >
              <option value="FEMEA">Fêmea</option>
              <option value="MACHO">Macho</option>
            </select>
          </Fld>
          <Fld label="Grupo / fase">
            <select
              data-testid="edit-grupo"
              value={f.grupo}
              onChange={(e) => set("grupo", e.target.value)}
              className="input"
            >
              {GRUPO_OPTIONS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </Fld>
          <Fld label="Raça">
            <input
              data-testid="edit-raca"
              value={f.raca}
              onChange={(e) => set("raca", e.target.value)}
              className="input"
              placeholder="Ex: Holandesa, Gir, Jersey"
            />
          </Fld>
          <Fld label="Data de nascimento">
            <input
              type="date"
              data-testid="edit-nascimento"
              value={f.data_nascimento}
              onChange={(e) => set("data_nascimento", e.target.value)}
              className="input"
            />
          </Fld>
          <Fld label="Status">
            <select
              data-testid="edit-status"
              value={f.status}
              onChange={(e) => set("status", e.target.value)}
              className="input"
            >
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </Fld>
          <Fld label="Mãe">
            <select
              data-testid="edit-mae"
              value={f.mae_id}
              onChange={(e) => set("mae_id", e.target.value)}
              className="input"
            >
              <option value="">— nenhuma —</option>
              {mothers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.numero} {m.nome ? `- ${m.nome}` : ""}
                </option>
              ))}
            </select>
          </Fld>
          <Fld label="Touro (pai)">
            <input
              data-testid="edit-touro"
              value={f.pai_touro}
              onChange={(e) => set("pai_touro", e.target.value)}
              className="input"
              placeholder="Código/nome do touro"
            />
          </Fld>
          <div className="md:col-span-2">
            <Fld label="Observações">
              <textarea
                data-testid="edit-observacoes"
                value={f.observacoes}
                onChange={(e) => set("observacoes", e.target.value)}
                className="input min-h-[80px]"
              />
            </Fld>
          </div>

          <div className="md:col-span-2 flex justify-end gap-3 pt-2 border-t border-[#E2E0D8]">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancelar
            </button>
            <button
              type="submit"
              data-testid="submit-edit-animal"
              disabled={saving}
              className="btn-primary"
            >
              {saving ? "Salvando…" : "Salvar alterações"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PesoChart({ pesos }) {
  if (!pesos || pesos.length < 1) return null;
  const sorted = [...pesos].sort((a, b) => (a.data > b.data ? 1 : -1));
  const chartData = sorted.map((p) => ({
    data: p.data.slice(0, 10),
    peso: Number(p.peso_kg),
  }));

  // GMD (ganho médio diário) entre primeiro e último registro
  let gmd = null;
  if (sorted.length >= 2) {
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const d1 = parseLocalDate(first.data);
    const d2 = parseLocalDate(last.data);
    const dias = d1 && d2 ? Math.round((d2 - d1) / 86400000) : 0;
    if (dias > 0) {
      const gmd_kg = (last.peso_kg - first.peso_kg) / dias;
      gmd = { kg: gmd_kg, dias };
    }
  }
  const ultimo = sorted[sorted.length - 1];

  return (
    <div data-testid="peso-chart" className="bg-white border border-[#E2E0D8] rounded-sm p-5 mb-6">
      <div className="flex flex-wrap items-baseline justify-between mb-4 gap-3">
        <h3 className="font-heading font-bold text-lg tracking-tight">
          Curva de peso
        </h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <span>
            <span className="text-[11px] uppercase tracking-wider text-[#4E544B]">
              Último:
            </span>{" "}
            <b>{Number(ultimo.peso_kg).toFixed(1)} kg</b>
          </span>
          {gmd && (
            <span>
              <span className="text-[11px] uppercase tracking-wider text-[#4E544B]">
                GMD ({gmd.dias}d):
              </span>{" "}
              <b className={gmd.kg >= 0 ? "text-[#2A5A3B]" : "text-[#C05746]"}>
                {gmd.kg >= 0 ? "+" : ""}
                {(gmd.kg * 1000).toFixed(0)} g/dia
              </b>
            </span>
          )}
        </div>
      </div>
      <div className="h-64">
        <ResponsiveContainer>
          <LineChart data={chartData}>
            <CartesianGrid stroke="#E2E0D8" vertical={false} />
            <XAxis
              dataKey="data"
              tickFormatter={(d) =>
                (parseLocalDate(d) || new Date(d)).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "2-digit",
                })
              }
              stroke="#4E544B"
              fontSize={11}
            />
            <YAxis stroke="#4E544B" fontSize={11} domain={["auto", "auto"]} unit=" kg" />
            <Tooltip
              contentStyle={{
                background: "#fff",
                border: "1px solid #E2E0D8",
                borderRadius: 2,
              }}
              formatter={(v) => [`${Number(v).toFixed(1)} kg`, "Peso"]}
              labelFormatter={(d) => formatDate(d)}
            />
            <Line
              type="monotone"
              dataKey="peso"
              stroke="#2A5A3B"
              strokeWidth={2.5}
              dot={{ r: 4, fill: "#2A5A3B" }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-[#4E544B]">
        {label}
      </div>
      <div className="text-sm font-semibold mt-1">{value}</div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, count }) {
  return (
    <div className="bg-white border border-[#E2E0D8] rounded-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#4E544B]">
          {label}
        </span>
        <Icon size={20} weight="duotone" color="#2A5A3B" />
      </div>
      <div className="font-heading font-black text-3xl">{count}</div>
    </div>
  );
}

function LactationChart({ producoes, partos }) {
  if (!producoes || producoes.length < 1) return null;

  // Somar todos os litros por dia (mesmo com múltiplas ordenhas)
  const byDay = {};
  for (const p of producoes) {
    const d = (p.data || "").slice(0, 10);
    if (!d) continue;
    byDay[d] = (byDay[d] || 0) + Number(p.litros || 0);
  }
  const days = Object.keys(byDay).sort();
  const totalLitros = producoes.reduce(
    (acc, p) => acc + Number(p.litros || 0),
    0,
  );
  const diasRegistrados = days.length;
  const mediaDia = diasRegistrados > 0 ? totalLitros / diasRegistrados : 0;
  const pico = days.reduce(
    (acc, d) => (byDay[d] > acc.litros ? { data: d, litros: byDay[d] } : acc),
    { data: null, litros: 0 },
  );

  // Último parto (partos vem ordenado desc no history)
  const ultimoParto = partos && partos.length > 0 ? partos[0] : null;
  const partoDate = ultimoParto ? parseLocalDate(ultimoParto.data) : null;

  // Somente considera DEL para produções após o parto
  const chartData = days.map((d) => {
    const dt = parseLocalDate(d);
    const del =
      partoDate && dt && dt >= partoDate
        ? Math.round((dt - partoDate) / 86400000)
        : null;
    return { data: d, litros: Number(byDay[d].toFixed(2)), del };
  });

  const chartComDel = chartData.filter((c) => c.del !== null && c.del >= 0);

  return (
    <div
      data-testid="lactation-chart"
      className="bg-white border border-[#E2E0D8] rounded-sm p-5 mb-6"
    >
      <div className="flex flex-wrap items-baseline justify-between mb-4 gap-3">
        <h3 className="font-heading font-bold text-lg tracking-tight">
          Curva de lactação
        </h3>
        <div className="flex flex-wrap gap-5 text-sm">
          <span>
            <span className="text-[11px] uppercase tracking-wider text-[#4E544B]">
              Total:
            </span>{" "}
            <b data-testid="prod-total">{totalLitros.toFixed(1)} L</b>
          </span>
          <span>
            <span className="text-[11px] uppercase tracking-wider text-[#4E544B]">
              Média/dia:
            </span>{" "}
            <b>{mediaDia.toFixed(1)} L</b>
          </span>
          <span>
            <span className="text-[11px] uppercase tracking-wider text-[#4E544B]">
              Dias registrados:
            </span>{" "}
            <b>{diasRegistrados}</b>
          </span>
          {pico.data && (
            <span>
              <span className="text-[11px] uppercase tracking-wider text-[#4E544B]">
                Pico:
              </span>{" "}
              <b className="text-[#2A5A3B]">{pico.litros.toFixed(1)} L</b>
            </span>
          )}
        </div>
      </div>

      {chartComDel.length >= 1 ? (
        <>
          <div className="text-xs text-[#4E544B] mb-2">
            Eixo X: dias em lactação (DEL) desde{" "}
            {ultimoParto ? formatDate(ultimoParto.data) : "-"}
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={chartComDel}>
                <CartesianGrid stroke="#E2E0D8" vertical={false} />
                <XAxis
                  dataKey="del"
                  type="number"
                  domain={[0, "dataMax"]}
                  stroke="#4E544B"
                  fontSize={11}
                  label={{
                    value: "DEL (dias)",
                    position: "insideBottom",
                    offset: -2,
                    fontSize: 11,
                    fill: "#4E544B",
                  }}
                />
                <YAxis
                  stroke="#4E544B"
                  fontSize={11}
                  domain={[0, "auto"]}
                  unit=" L"
                />
                <Tooltip
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #E2E0D8",
                    borderRadius: 2,
                  }}
                  formatter={(v) => [`${Number(v).toFixed(2)} L`, "Produção"]}
                  labelFormatter={(del) => `${del} dias em lactação`}
                />
                <Line
                  type="monotone"
                  dataKey="litros"
                  stroke="#2A5A3B"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: "#2A5A3B" }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : (
        <div className="text-sm text-[#4E544B] py-4">
          {ultimoParto
            ? "Sem produção registrada após o último parto ainda."
            : "Sem parto registrado — não é possível calcular DEL. Um gráfico completo aparecerá após registrar um parto."}
        </div>
      )}
    </div>
  );
}

function CCSChart({ ccs }) {
  if (!ccs || ccs.length < 1) return null;
  const sorted = [...ccs].sort((a, b) => (a.data > b.data ? 1 : -1));
  const chartData = sorted.map((c) => ({
    data: c.data.slice(0, 10),
    ccs: Number(c.ccs_mil_ml || 0),
  }));
  const ultimo = sorted[sorted.length - 1];
  const val = Number(ultimo.ccs_mil_ml || 0);
  let status = { label: "Normal", color: "#2A5A3B", bg: "#E9F5EC" };
  if (val >= 500)
    status = { label: "Alta — mastite provável", color: "#C05746", bg: "#FDEDED" };
  else if (val >= 200)
    status = { label: "Suspeita subclínica", color: "#8B5A00", bg: "#FFF6E5" };

  return (
    <div
      data-testid="ccs-chart"
      className="bg-white border border-[#E2E0D8] rounded-sm p-5 mb-6"
    >
      <div className="flex flex-wrap items-baseline justify-between mb-4 gap-3">
        <h3 className="font-heading font-bold text-lg tracking-tight">
          Contagem de células somáticas (CCS)
        </h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <span>
            <span className="text-[11px] uppercase tracking-wider text-[#4E544B]">
              Última leitura:
            </span>{" "}
            <b>{val.toFixed(0)} mil/mL</b>
          </span>
          <span
            className="px-2 py-0.5 rounded-sm text-xs font-bold uppercase tracking-wide"
            style={{ background: status.bg, color: status.color }}
          >
            {status.label}
          </span>
        </div>
      </div>
      <div className="text-xs text-[#4E544B] mb-2">
        Referência: até 200 mil/mL = normal · 200–500 = suspeita subclínica · acima
        de 500 = mastite provável
      </div>
      <div className="h-56">
        <ResponsiveContainer>
          <LineChart data={chartData}>
            <CartesianGrid stroke="#E2E0D8" vertical={false} />
            <XAxis
              dataKey="data"
              tickFormatter={(d) =>
                (parseLocalDate(d) || new Date(d)).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "2-digit",
                })
              }
              stroke="#4E544B"
              fontSize={11}
            />
            <YAxis
              stroke="#4E544B"
              fontSize={11}
              domain={[0, (dataMax) => Math.max(600, dataMax)]}
              unit=" mil"
            />
            <Tooltip
              contentStyle={{
                background: "#fff",
                border: "1px solid #E2E0D8",
                borderRadius: 2,
              }}
              formatter={(v) => [`${Number(v).toFixed(0)} mil/mL`, "CCS"]}
              labelFormatter={(d) => formatDate(d)}
            />
            <Line
              type="monotone"
              dataKey="ccs"
              stroke="#C05746"
              strokeWidth={2.5}
              dot={{ r: 4, fill: "#C05746" }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

